import { Transition } from '@headlessui/react';
import { ExclamationIcon, XIcon } from '@heroicons/react/outline';
import {
  Auth0Role,
  IExportTCRCampaignPhoneNumbersRequest,
  IHttpResponse,
  ITCRCampaignDetails,
} from '@gr/shared/models';
import { Fragment, useState } from 'react';
import { axiosPost } from '../../authAxios';
import useProviders from '../../hooks/useProviders';
import useRoles from '../../hooks/useRoles';
import { downloadFileFromUrl } from '../../providers/utility.provider';
import { Button } from '../shared/Buttons/Button';
import { ButtonVariantEnum } from '../shared/Buttons/types';
import { DetailsPanel } from '../shared/DetailsPanel/DetailsPanel';
import { DetailsPanelSize, IButtonOptions } from '../shared/DetailsPanel/types';
import { IDropdownValue } from '../shared/Form/Dropdown';
import ActiveNumbersCard from './ActiveNumbersCard';
import PurchaseNumbersCard from './PurchaseNumbersCard';
import TCRCampaignForm from './TCRCampaignForm';
import { ITCRCampaignForm } from './types';

interface ITCRCampaignDetailsPanelProps {
  show: boolean;
  loading: boolean;
  toggleLoading: boolean;
  updateLoading: boolean;
  selectedItem?: ITCRCampaignDetails;
  errorMessage?: string;
  forceUpdateWarning?: string;
  onClosePanel: () => void;
  handleSubmit: (formData: ITCRCampaignForm) => Promise<void>;
  handleToggle: () => Promise<void>;
  handleUpdate: (formData: ITCRCampaignForm) => Promise<void>;
  onForceUpdate: () => Promise<void>;
  onDismissForceWarning: () => void;
}

const TCRCampaignDetailsPanel = ({
  show,
  loading,
  toggleLoading,
  updateLoading,
  selectedItem,
  errorMessage,
  forceUpdateWarning,
  onClosePanel,
  handleToggle,
  handleSubmit,
  handleUpdate,
  onForceUpdate,
  onDismissForceWarning,
}: ITCRCampaignDetailsPanelProps) => {
  const [{ data: providerData, loading: providerLoading, error: providerError }] = useProviders();
  const [isExporting, setIsExporting] = useState(false);
  const [exportErrorMessage, setExportErrorMessage] = useState(undefined);

  const canExport = useRoles([Auth0Role.GR_ADMIN]);

  const providers: IDropdownValue[] =
    providerData?.data.map((provider) => ({
      label: provider.name ?? '',
      value: provider.id ?? '',
    })) ?? [];

  const exportPhoneNumbers = async () => {
    if (selectedItem) {
      try {
        const { id, clientName, providerName, campaignType } = selectedItem;

        const request: IExportTCRCampaignPhoneNumbersRequest = {
          id,
          clientName,
          providerName,
          campaignType,
        };

        setIsExporting(true);
        const {
          data: { data: signedUrl },
        } = await axiosPost<IHttpResponse<string>>('/tcr-campaigns-phone-numbers-export', request);
        downloadFileFromUrl(signedUrl);
      } catch (err: any) {
        console.error(err);
        setExportErrorMessage(err?.response?.data?.message);
      } finally {
        setIsExporting(false);
      }
    }
  };

  const saveInProgress = loading || updateLoading;
  const leftActionsBlocked = saveInProgress || toggleLoading;

  const leftPanelButtonOptions: IButtonOptions[] = [
    {
      text: selectedItem?.isActive ? 'Disable' : 'Enable',
      visible: !!selectedItem,
      onClick: handleToggle,
      variant: selectedItem?.isActive ? ButtonVariantEnum.DELETE : ButtonVariantEnum.SECONDARY,
      loading: toggleLoading,
      disabled: saveInProgress,
    },
    {
      text: 'Export Phone Numbers',
      visible: !!selectedItem && canExport,
      onClick: () => exportPhoneNumbers(),
      variant: ButtonVariantEnum.SECONDARY,
      loading: isExporting,
      hideConfirmation: true,
      disabled: leftActionsBlocked,
    },
  ];

  const onFormSubmit = selectedItem ? handleUpdate : handleSubmit;

  return (
    <DetailsPanel
      item={selectedItem}
      title={selectedItem ? 'Edit TCR Campaign' : 'Add TCR Campaign'}
      formId="tcr-campaign-form"
      size={DetailsPanelSize.Medium}
      show={show}
      closePanel={onClosePanel}
      isReadOnly={false}
      loading={saveInProgress}
      isDisabled={toggleLoading}
      saveDisabledDuringLeftConfirmation={!!selectedItem}
      errorMessage={exportErrorMessage || errorMessage}
      leftPanelButtonOptions={leftPanelButtonOptions}
    >
      <div>
        <Transition
          appear
          show={!!forceUpdateWarning}
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div className="relative flex flex-col w-full p-4 mb-4 text-left transition-all transform bg-amber-50 border border-amber-200 rounded-md shadow-md">
            <Button
              className="absolute top-0 right-0 rounded-tr-md hover:bg-amber-100"
              variant={ButtonVariantEnum.TEXT_DEFAULT}
              leadingIcon={<XIcon className="w-4 h-4 text-amber-400" />}
              onClick={onDismissForceWarning}
            />
            <div className="flex items-start gap-3">
              <ExclamationIcon className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">Active Campaign Warning</p>
                <p className="mt-1 text-sm text-amber-700">{forceUpdateWarning}</p>
                <p className="mt-2 text-sm font-semibold text-amber-800">Do you want to update anyway?</p>
                <Button
                  onClick={onForceUpdate}
                  disabled={updateLoading}
                  type="button"
                  variant={ButtonVariantEnum.SECONDARY}
                  className="mt-3 border border-sky-200 rounded-md"
                >
                  Confirm Update
                </Button>
              </div>
            </div>
          </div>
        </Transition>

        <TCRCampaignForm item={selectedItem} onSubmit={onFormSubmit} providers={providers} />
        {selectedItem && <PurchaseNumbersCard selectedItem={selectedItem} closePanel={onClosePanel} />}
        {selectedItem && (selectedItem?.activeNumbers ?? 0) > 0 && <ActiveNumbersCard selectedItem={selectedItem} />}
      </div>
    </DetailsPanel>
  );
};

export default TCRCampaignDetailsPanel;
